
import {
  ref,
  computed,
  defineComponent,
  PropType,
  onMounted,
  watch
} from 'vue'

import { useStore } from 'vuex'
import { Entry, RequestHandler, UUID } from '@Protium/vue-ozone-utils'
import { ProductView } from './interfaces'
import ProductList from './ProductList.vue'
// import AddressData from './AddressData.vue'
import AddressEditor from '@/components/AddressEditor.vue'
import OperationalData from './OperationalData.vue'
import OrderEventTimeline from '@/pages/Dashboard/components/OrderEventTimeline.vue'
import OrderFulfillmentDetails from '@/pages/Dashboard/components/OrderFulfillmentDetails.vue'
import OrderErrorDetails from '@/pages/Dashboard/components/OrderErrorDetails.vue'
// import PackingData from './PackingData.vue'
// import { Fulfillment, OrderProduct, OrderStatusInfo, ShipmentLabel } from '@/models/interfaces/order'
import { CtOrder } from '@/models/interfaces/content-type'
// import { Address } from '@/models/generics'
import { ItemViewMode } from '@/constants'
// import { debounce } from '@/utils'
import { useQuasar } from 'quasar'
import axios from 'axios'
import useDocParser from '@/pages/composables/useDocParser'

enum OrderDetailView {
  Products = 'Product Details',
  Fulfillments = 'Customer Data',
  Events = 'Events'
}

enum DetailsViewMode {
  Default = 'default',
  FullscreenCard = 'fullscreen-card'
}

export default defineComponent({
  name: 'OrderDetails',
  components: { ProductList, OperationalData, OrderEventTimeline, OrderFulfillmentDetails, OrderErrorDetails, AddressEditor },
  props: {
    header: {
      type: String,
      default: null
    },
    payload: {
      type: Object as PropType<Partial<CtOrder>>,
    },
    id: {
      type: String as PropType<UUID>,
    },
    actions: {
      type: Object,
      default: () => ({})
    },
    request: {
      type: Object as PropType<RequestHandler<Entry>>
    },
    showIssues: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    shouldLoad: {
      type: Boolean,
      default: true
    },
    scaleWithView: {
      type: Boolean,
      default: false
    },
    collections: {
      type: Object as PropType<Record<string, Array<Entry>>>,
      default: () => ({})
    },
    mode: {
      type: String as PropType<DetailsViewMode>
    },
    highlight: {
      type: Array as PropType<Array<string>>,
      default: () => ([])
    },
    productsView: {
      type: String as PropType<ProductView>
    },
    itemView: {
      type: Number as PropType<ItemViewMode>,
      default: ItemViewMode.Default
    }
  },
  emits: ['update:payload', 'update:highlight', 'resolved', 'resizeheight'],
  setup (props, { emit }) {
    const loaded = ref((props.shouldLoad !== true && props.id) || (props.shouldLoad !== true && !props.id && props.payload))
    const $store = useStore()
    const $directr = $store.getters['Services/Directr']
    const $q = useQuasar()

    const detailsContainer = ref<Element>()

    const products = ref(props.payload?.products)
    const packing = ref(props.payload?.packing)
    const address = ref(props.payload?.shipping_address)

    const fulfillmentData = ref({ items: props.payload?.fulfillments, labels: props.payload?.shipments })
    const operationalNotes = ref(props.payload?.operational_notes)
    const meta = ref(props.payload?.meta || {})
    const statusInfo = ref(props.payload?.status_info)
    const isMobile = computed(() => $q.screen.lt.md)

    const { docData, showingDocs, showDocuments } = useDocParser()


    watch(() => detailsContainer.value, (el) => {
      if (!props.scaleWithView) return
      if (el && isMobile.value)
        (el as HTMLElement).style.height = `${$q.screen.height - (detailsContainer.value ? detailsContainer.value.getBoundingClientRect().y : 200)}px`
    })

    // const getCurrentStatus = debounce((item) => {
    //   emit('resizeheight', detailsContainer.value)
    // }, 400)

    // onMounted(() => {
    //   getCurrentStatus(products)
    // })

    const operationalData = computed({
      get: () => {
        return { fulfillments: fulfillmentData.value, notes: operationalNotes.value, address: address.value, statusInfo: statusInfo.value, meta: meta.value }
      },
      set: (value) => {
        if (!value) return
        if (value.fulfillments) fulfillmentData.value = value.fulfillments
        if (value.notes) operationalNotes.value = value.notes
        if (value.address) address.value = value.address
        if (value.statusInfo) statusInfo.value = value.statusInfo
        if (value.meta) meta.value = value.meta
      }
    })

    watch(() => props.payload, value => setRefs(value))

    const active = ref<OrderDetailView>(OrderDetailView.Products)
    const editingOperationalData = ref(false)

    function setActive (view: OrderDetailView, edit = false) {
      active.value = view
      editingOperationalData.value = edit
    }

    function toggleActive (edit = false) {
      active.value = (active.value === OrderDetailView.Products)
        ? OrderDetailView.Fulfillments
        : OrderDetailView.Products
      editingOperationalData.value = edit
    }

    async function loadEntry () {
      loaded.value = false
      if (!props.request) throw Error('OrderDetails attempted to load entry, but no request prop was passed')
      const id = props.id || props.payload?.sys?.id

      const entry = await props.request.get('id', id)
      loaded.value = true
      emit('resolved', entry)
    }

    function setRefs (value = props.payload) {
      // console.log('setRefs', props.payload)
      products.value = value?.products
      packing.value = value?.packing
      address.value = value?.shipping_address

      fulfillmentData.value = { items: value?.fulfillments, labels: value?.shipments }
      operationalNotes.value = value?.operational_notes
      meta.value = value?.meta || {}
      statusInfo.value = value?.status_info
    }

    onMounted(() => {
      if (props.shouldLoad && props.id)
        loadEntry().catch(console.error)
    })

    watch(() => packing.value, payload => {
      emit('update:payload', { ...props.payload, packing: payload })
    })

    watch(() => products.value, payload => {
      emit('update:payload', { ...props.payload, products: payload })
    })

    function emitProductsAndPackaging (data) {
      const payload = {...props.payload, status_info: data.status_info, status: data.status, products: data.products || props.payload?.products, packing: data.packing || props.payload?.packing }
      emit('update:payload', { ...payload })

      if (props.id)
        loadEntry().catch(console.error)
    }

    return {
      docData,
      showDocuments,
      showingDocs,
      loaded,
      OrderDetailView,
      patchAddress: $directr.order.patchAddress,
      views: Object.values(OrderDetailView),
      active,
      setActive,
      toggleActive,
      operationalData,
      editingOperationalData,
      emitProductsAndPackaging,
      products,
      packing,
      detailsContainer,
      address,
      fulfillmentData,
      meta,
      operationalNotes,
      statusInfo,
      findFulfillment: (id) => {
        return {
          fulfillment: operationalData.value?.fulfillments?.items?.find(f => f.id === id),
          labels: operationalData.value?.fulfillments?.labels?.find((f => f.fulfillment === id))
        }
      },
      requestHandler: props.request || $store.getters['Services/Directr'],
      highlightPacked: computed({
        get: () => props.highlight,
        set: value => emit('update:highlight', value)
      })
    }
  }
})
